import { createApi } from "@reduxjs/toolkit/query/react";

import type { IApiResponse } from "@/shared/interfaces/api-response.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";

export type BroadcastChannel = "SLACK" | "EMAIL";

export interface IBroadcast {
  id: string;
  name: string;
  channel: BroadcastChannel;
  enabled: boolean;
  destination: string;
  types: string[];
  priorities: string[];
  initiators: string[];
  districtIds: string[];
  lastDeliveryAt: string | null;
  lastDeliveryOk: boolean | null;
  lastDeliveryError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateBroadcastPayload {
  name: string;
  channel: BroadcastChannel;
  config: Record<string, unknown>;
  enabled?: boolean;
  types?: string[];
  priorities?: string[];
  initiators?: string[];
  districtIds?: string[];
}

export interface IUpdateBroadcastPayload {
  name?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
  types?: string[];
  priorities?: string[];
  initiators?: string[];
  districtIds?: string[];
}

const BASE = "/admin/chat-alerts/broadcasts";

export const broadcastsApi = createApi({
  reducerPath: "broadcastsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Broadcasts],
  endpoints: (builder) => ({
    getBroadcasts: builder.query<IBroadcast[], void>({
      query: () => ({ url: BASE, method: "GET" }),
      providesTags: [ApiTags.Broadcasts],
      transformResponse: (result: IApiResponse<{ items: IBroadcast[] }>) => result.data.items ?? [],
    }),
    createBroadcast: builder.mutation<IBroadcast, ICreateBroadcastPayload>({
      query: (body) => ({ url: BASE, method: "POST", body }),
      invalidatesTags: [ApiTags.Broadcasts],
      transformResponse: (result: IApiResponse<IBroadcast>) => result.data,
    }),
    updateBroadcast: builder.mutation<IBroadcast, { id: string; body: IUpdateBroadcastPayload }>({
      query: ({ id, body }) => ({ url: `${BASE}/${id}`, method: "PATCH", body }),
      invalidatesTags: [ApiTags.Broadcasts],
      transformResponse: (result: IApiResponse<IBroadcast>) => result.data,
    }),
    deleteBroadcast: builder.mutation<void, string>({
      query: (id) => ({ url: `${BASE}/${id}`, method: "DELETE" }),
      invalidatesTags: [ApiTags.Broadcasts],
    }),
    testBroadcast: builder.mutation<{ ok: boolean; error: string | null }, string>({
      query: (id) => ({ url: `${BASE}/${id}/test`, method: "POST" }),
      invalidatesTags: [ApiTags.Broadcasts],
      transformResponse: (result: IApiResponse<{ ok: boolean; error: string | null }>) => result.data,
    }),
  }),
});

export default broadcastsApi;

export const {
  useGetBroadcastsQuery,
  useCreateBroadcastMutation,
  useUpdateBroadcastMutation,
  useDeleteBroadcastMutation,
  useTestBroadcastMutation,
} = broadcastsApi;
